import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getVisibleGroups, pageHref } from "./nav-config";

const SEQUENCE_TIMEOUT_MS = 1200;

// Something else owns the keyboard: a text field, an open modal or sheet,
// or a SweetAlert dialog.
const shouldIgnore = (e) => {
  if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey) return true;
  const target = e.target;
  if (
    target instanceof HTMLElement &&
    (target.isContentEditable ||
      ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))
  )
    return true;
  return Boolean(
    document.querySelector(
      '[aria-modal="true"], .modal-container, .swal2-container'
    )
  );
};

export const getShortcutItems = (permissions) =>
  getVisibleGroups(permissions)
    .flatMap((group) => group.items)
    .filter((item) => item.shortcut);

// Requests-wide shortcuts: "g" then a letter jumps to a page, "n" starts a
// new request, "?" opens the cheat sheet.
export function useRequestShortcuts({
  pagePermissions,
  enabled,
  onNewRequest,
  onShowHelp,
}) {
  const navigate = useNavigate();
  const pendingGRef = useRef(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;

    const items = getShortcutItems(pagePermissions);

    const resetSequence = () => {
      pendingGRef.current = false;
      clearTimeout(timerRef.current);
    };

    const handleKeyDown = (e) => {
      if (shouldIgnore(e)) return resetSequence();
      const key = e.key.toLowerCase();

      if (pendingGRef.current) {
        resetSequence();
        const item = items.find((navItem) => navItem.shortcut === key);
        if (item) {
          e.preventDefault();
          navigate(pageHref(item.key));
        }
        return;
      }

      if (key === "g") {
        pendingGRef.current = true;
        timerRef.current = setTimeout(resetSequence, SEQUENCE_TIMEOUT_MS);
      } else if (key === "n") {
        e.preventDefault();
        onNewRequest();
      } else if (e.key === "?") {
        e.preventDefault();
        onShowHelp();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      resetSequence();
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [enabled, pagePermissions, navigate, onNewRequest, onShowHelp]);
}

export function ShortcutsDialog({ pagePermissions, onClose }) {
  const closeRef = useRef(null);
  const items = getShortcutItems(pagePermissions);

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    closeRef.current?.focus();

    const handleKeyDown = (e) => {
      if (e.key === "Escape" || e.key === "?") {
        e.preventDefault();
        onClose();
      } else if (e.key === "Tab") {
        // The close button is the only control, so keep focus on it.
        e.preventDefault();
        closeRef.current?.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      previouslyFocused?.focus?.();
    };
  }, [onClose]);

  const rows = [
    ...items.map((item) => ({ keys: ["g", item.shortcut], label: item.label })),
    { keys: ["n"], label: "New request" },
    { keys: ["?"], label: "Show this list" },
  ];

  return (
    <div className="rq-shortcuts">
      <div className="rq-shortcuts__backdrop" onClick={onClose} aria-hidden="true" />
      <div
        className="rq-shortcuts__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rq-shortcuts-title"
      >
        <div className="rq-shortcuts__head">
          <h2 id="rq-shortcuts-title" className="rq-shortcuts__title">
            Keyboard shortcuts
          </h2>
          <button
            ref={closeRef}
            type="button"
            className="rq-shortcuts__close"
            onClick={onClose}
            aria-label="Close keyboard shortcuts"
          >
            <i className="fa fa-times" aria-hidden="true"></i>
          </button>
        </div>
        <dl className="rq-shortcuts__list">
          {rows.map((row) => (
            <div className="rq-shortcuts__row" key={row.label}>
              <dt className="rq-shortcuts__label">{row.label}</dt>
              <dd className="rq-shortcuts__keys">
                {row.keys.map((key, index) => (
                  <React.Fragment key={key}>
                    {index > 0 && <span className="rq-shortcuts__then">then</span>}
                    <kbd>{key}</kbd>
                  </React.Fragment>
                ))}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}
